import { Action, combineReducers, createReducer, on } from '@ngrx/store';

import { generateHistoricalPrescriptionsByDate } from '../utils/generate-historical-prescriptions.function';
import { addPrescriptionDraft, clearPrescriptionDrafts, updatePrescriptionDraft } from './prescriptions-drafts.actions';
import { prescriptionsDraftsReducer } from './prescriptions-drafts.reducer';
import {
  addFreeForm,
  addPrescription,
  clearGuarantee,
  clearPrescriptions,
  clearTargetPrescription,
  deletePrescription,
  editPrescription,
  fetchPrescriptions,
  fetchPrescriptionsFailure,
  fetchPrescriptionsSuccess,
  incrementPrescriptionsPage,
  processPrescriptionsDrafts,
  processPrescriptionsDraftsFailure,
  processPrescriptionsDraftsSuccess,
  removeFreeForm,
  removePrescription,
  resetPrescriptionsState,
  saveAppointment,
  saveGuarantee,
  savePrescription,
  setPrescriptionsInitRequired,
  setPrescriptionsPage,
  setPrescriptionsSearchFilter,
  setTargetPrescription,
  silentFetchPrescriptions,
  silentFetchPrescriptionsSuccess,
  startPrescriptionsPolling,
  stopPrescriptionsPolling,
  updateFreeForm,
} from './prescriptions.actions';
import { PrescriptionsFeatureState, initialGuaranteeState, initialPrescriptionsState } from './prescriptions.state';

const prescriptionsReducer = createReducer(
  initialPrescriptionsState,
  on(savePrescription, state => ({ ...state, prescriptionSaved: true })),
  on(editPrescription, (state, { editData }) => ({ ...state, prescriptionEdited: editData })),
  on(deletePrescription, (state, { deletionData }) => ({ ...state, prescriptionDeleted: deletionData })),
  on(setPrescriptionsInitRequired, (state, { initRequired }) => ({ ...state, initRequired })),
  on(setPrescriptionsSearchFilter, (state, { search }) => ({ ...state, guaranteesFilters: { ...state.guaranteesFilters, search } })),
  on(setPrescriptionsPage, (state, { page }) => ({ ...state, page })),
  on(incrementPrescriptionsPage, state => ({ ...state, page: state.page + 1, guaranteesFetching: true })),
  on(fetchPrescriptions, state => ({ ...state, loading: true, guaranteesFetching: true })),
  on(fetchPrescriptionsSuccess, (state, { pagedResponse }) => ({
    ...state,
    count: pagedResponse.count,
    page: pagedResponse.page,
    pageSize: pagedResponse.pageSize,
    hasNextPage: pagedResponse.hasNextPage,
    hasPreviousPage: pagedResponse.hasPreviousPage,
    guarantees: [...state.guarantees, ...generateHistoricalPrescriptionsByDate(pagedResponse)],
    loading: false,
    guaranteesFetching: false,
  })),
  on(fetchPrescriptionsFailure, state => ({ ...state, loading: false, guaranteesFetching: false })),
  on(addPrescriptionDraft, state => ({ ...state, prescriptionDraftSubmitted: false })),
  on(updatePrescriptionDraft, state => ({ ...state, prescriptionDraftSubmitted: false })),
  on(processPrescriptionsDrafts, state => ({ ...state, loading: true, draftsProcessing: true })),
  on(processPrescriptionsDraftsSuccess, state => ({ ...state, loading: false, draftsProcessing: false })),
  on(processPrescriptionsDraftsFailure, state => ({ ...state, loading: false, draftsProcessing: false })),
  on(clearPrescriptions, state => ({
    ...state,
    count: initialPrescriptionsState.count,
    page: initialPrescriptionsState.page,
    pageSize: initialPrescriptionsState.pageSize,
    hasNextPage: initialPrescriptionsState.hasNextPage,
    hasPreviousPage: initialPrescriptionsState.hasPreviousPage,
    guarantees: initialPrescriptionsState.guarantees,
    loading: initialPrescriptionsState.loading,
    guaranteesFetching: initialPrescriptionsState.guaranteesFetching,
  })),
  on(clearPrescriptionDrafts, state => state),
  on(setTargetPrescription, (state, { prescriptionId }) => ({ ...state, targetPrescription: { prescriptionId } })),
  on(clearTargetPrescription, state => ({ ...state, targetPrescription: null })),
  on(resetPrescriptionsState, () => initialPrescriptionsState),
  on(silentFetchPrescriptions, state => ({ ...state })),
  on(silentFetchPrescriptionsSuccess, (state, { pagedResponse }) => {
    const newGuarantees = generateHistoricalPrescriptionsByDate(pagedResponse);

    const mergedGuarantees = state.guarantees.map(existingGuarantee => {
      const updatedGuarantee = newGuarantees.find(g => g.id === existingGuarantee.id);

      if (updatedGuarantee) {
        return {
          ...existingGuarantee,
          prescriptions: updatedGuarantee.prescriptions,
          status: updatedGuarantee.status,
          lastUpdateDate: updatedGuarantee.lastUpdateDate,
        };
      } else return existingGuarantee;
    });

    // Add any new guarantees that weren't in the existing list
    const existingIds = new Set(state.guarantees.map(g => g.id));
    const newGuaranteesToAdd = newGuarantees.filter(g => !existingIds.has(g.id));

    return {
      ...state,
      count: pagedResponse.count,
      page: pagedResponse.page,
      pageSize: pagedResponse.pageSize,
      hasNextPage: pagedResponse.hasNextPage,
      hasPreviousPage: pagedResponse.hasPreviousPage,
      guarantees: [...newGuaranteesToAdd, ...mergedGuarantees],
    };
  }),
  on(startPrescriptionsPolling, state => ({ ...state, isPolling: true })),
  on(stopPrescriptionsPolling, state => ({ ...state, isPolling: false })),
  on(saveAppointment, (state, { appointmentId }) => ({
    ...state,
    guarantee: { ...state.guarantee, appointmentId },
  })),
  on(saveGuarantee, (state, { description, sendGuaranteeNotification }) => ({
    ...state,
    guarantee: { ...state.guarantee, description, sendGuaranteeNotification },
  })),
  on(addPrescription, (state, { prescription }) => ({
    ...state,
    guarantee: { ...state.guarantee, prescriptions: [...state.guarantee.prescriptions, prescription] },
  })),
  on(removePrescription, (state, { index }) => ({
    ...state,
    guarantee: { ...state.guarantee, prescriptions: state.guarantee.prescriptions.filter((_, i) => i !== index) },
  })),
  on(addFreeForm, (state, { request }) => ({
    ...state,
    guarantee: { ...state.guarantee, freeForm: [...state.guarantee.freeForm, request] },
  })),
  on(updateFreeForm, (state, { index, request }) => ({
    ...state,
    guarantee: { ...state.guarantee, freeForm: state.guarantee.freeForm.map((item, i) => (i === index ? request : item)) },
  })),
  on(removeFreeForm, (state, { index }) => ({
    ...state,
    guarantee: { ...state.guarantee, freeForm: state.guarantee.freeForm.filter((_, i) => i !== index) },
  })),
  on(clearGuarantee, state => ({ ...state, guarantee: initialGuaranteeState }))
);

const combinedReducer = combineReducers<PrescriptionsFeatureState>({
  prescriptions: prescriptionsReducer,
  drafts: prescriptionsDraftsReducer,
});

export function prescriptionsFeatureReducer(state: PrescriptionsFeatureState | undefined, action: Action): PrescriptionsFeatureState {
  if (action.type === resetPrescriptionsState.type) {
    return combinedReducer(undefined, action);
  }
  return combinedReducer(state, action);
}
